import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { useToast } from '../context/ToastContext';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../context/AuthContext';
import { useConversations } from '../context/ConversationContext';
import { apiClient } from '../api/client';
import { wsClient } from '../api/websocket';

/** Knowledge scopes offered by the composer (design spec §10.2). */
export const KNOWLEDGE_SCOPES = ['company', 'all'];

/**
 * The scope is expressed to the model through the existing `personaPrompt`
 * lens channel of `chat:message`; the wire format has no dedicated field yet.
 * Kept here (not in the composer) so the whole chat path has one source of
 * truth for what "Firmenwissen" means.
 */
const SCOPE_LENS = {
  company:
    'Antworte ausschließlich auf Basis der bereitgestellten Firmenquellen. '
    + 'Wenn die Quellen die Frage nicht abdecken, sage das ausdrücklich, statt allgemeines Modellwissen zu ergänzen.',
  all: null,
};

/**
 * Chat state machine for the Etch chat surface.
 *
 * Beyond the plain streaming loop it owns three things the redesign added:
 *  - `chat:sources` — the per-turn source descriptors, which arrive BEFORE the
 *    first chunk so the source panel can fill while the answer streams,
 *  - trust metadata, persisted into `conversation_messages.metadata` and
 *    rehydrated on reload, so a reopened thread keeps its citations,
 *  - a retry that resumes the last question without losing the visible turn.
 */
export function useChat() {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingSources, setStreamingSources] = useState([]);
  const [sourcesPending, setSourcesPending] = useState(false);
  const [error, setError] = useState(null);
  const { sendMessage, cancelStream, onChunk, onComplete, onError, connected } = useWebSocket();
  const { selectedPersona } = useSession();
  const { user } = useAuth();
  const {
    currentId,
    setCurrentId,
    createConversation,
    requestAutoTitle,
    refresh: refreshConversations,
  } = useConversations();
  const toast = useToast();
  const streamingTextRef = useRef('');
  const streamingSourcesRef = useRef([]);
  const conversationIdRef = useRef(currentId);
  const turnCountRef = useRef(0);
  const personaConversationIdRef = useRef(null);
  const messagesRef = useRef([]);
  const skipNextReloadRef = useRef(false);
  const lastRequestRef = useRef(null);
  // Attribution has to be readable from the long-lived WebSocket callbacks,
  // which are registered once and would otherwise close over a stale persona.
  const personaRef = useRef(selectedPersona);

  useEffect(() => {
    conversationIdRef.current = currentId;
  }, [currentId]);

  useEffect(() => {
    personaRef.current = selectedPersona;
  }, [selectedPersona]);

  /**
   * Which persona answered *this* turn, as a descriptor stored on the message
   * and persisted into `conversation_messages.author_name` (migration 009).
   * Stamped at answer time so a later persona switch cannot relabel it.
   */
  const personaStamp = useCallback(() => {
    const persona = personaRef.current;
    return persona?.personaId || persona?.name
      ? { id: persona.personaId ?? null, name: persona.name ?? null }
      : null;
  }, []);

  /**
   * Persist an assistant turn that never reached `chat:complete` — a technical
   * error or a user cancellation. The success path persists in the onComplete
   * handler; without this, an interrupted answer would live in React state only
   * and vanish on the next reload or thread switch (spec §11.1, §10.2).
   */
  const persistInterruptedTurn = useCallback((content, sources, flags) => {
    const convId = conversationIdRef.current;
    if (!convId || !content) return;
    apiClient.appendConversationMessage(convId, {
      role: 'assistant',
      content,
      metadata: { sources: sources || [], persona: personaStamp(), ...flags },
    })
      .then(() => { turnCountRef.current += 1; })
      .catch(err => console.error('Failed to persist interrupted assistant message:', err));
  }, [personaStamp]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load messages when switching conversation (skipped right after create-from-ask)
  useEffect(() => {
    if (!currentId) {
      setMessages([]);
      turnCountRef.current = 0;
      personaConversationIdRef.current = null;
      return;
    }

    if (skipNextReloadRef.current) {
      skipNextReloadRef.current = false;
      return;
    }

    let cancelled = false;
    apiClient.getConversation(currentId)
      .then(({ conversation, messages: serverMsgs }) => {
        if (cancelled) return;
        const mapped = (serverMsgs || []).map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at),
          // Who asked / which persona answered, as recorded on the turn itself
          // (migration 009) — never re-derived from the session reading it.
          author: { id: m.author_user_id || null, name: m.author_name || null },
          persona: m.metadata?.persona
            || (m.role === 'assistant' && m.author_name ? { id: null, name: m.author_name } : null),
          queries: m.metadata?.queries || undefined,
          suggestions: m.metadata?.suggestions || [],
          billed: m.metadata?.billed || undefined,
          axiom: m.metadata?.axiom || undefined,
          notices: m.metadata?.notices || undefined,
          // Citation provenance survives a reload — a grounded answer must not
          // silently degrade into "Modellwissen" just because the tab was closed.
          sources: m.metadata?.sources || [],
          trust: m.metadata?.trust || null,
          // An interrupted answer stays marked as interrupted across a reload.
          partial: m.metadata?.partial || undefined,
          cancelled: m.metadata?.cancelled || undefined,
        }));
        setMessages(mapped);
        turnCountRef.current = mapped.length;
        personaConversationIdRef.current = conversation?.persona_conversation_id || null;
      })
      .catch(err => {
        if (cancelled) return;
        // A thread that is gone or belongs to another account (the id survives
        // in localStorage across a sign-in) is not an error to show — drop it
        // and fall back to a fresh conversation.
        if (err.status === 404) {
          setMessages([]);
          turnCountRef.current = 0;
          personaConversationIdRef.current = null;
          setCurrentId(null);
          return;
        }
        console.error('Failed to load conversation:', err);
        setError(err.message);
      });

    return () => { cancelled = true; };
  }, [currentId, setCurrentId]);

  // `chat:sources` is not (yet) forwarded by WebSocketContext, so we listen on
  // the client singleton directly. Emitted exactly once per turn, always,
  // strictly before the first chunk.
  useEffect(() => {
    const unsub = wsClient.on('chat:sources', (payload) => {
      const list = Array.isArray(payload?.sources) ? payload.sources : [];
      streamingSourcesRef.current = list;
      setStreamingSources(list);
      setSourcesPending(false);
    });
    return unsub;
  }, []);

  // WebSocket handlers (only used when persona NOT active)
  useEffect(() => {
    const unsubChunk = onChunk((text) => {
      streamingTextRef.current += text;
      setStreamingText(streamingTextRef.current);
    });

    const unsubComplete = onComplete(async ({ queriesExecuted, suggestions, sources, trust, notices }) => {
      const content = streamingTextRef.current;
      const resolvedSources = Array.isArray(sources) ? sources : streamingSourcesRef.current;

      const persona = personaStamp();

      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content,
        persona,
        queries: queriesExecuted,
        suggestions: suggestions || [],
        sources: resolvedSources,
        trust: trust || null,
        notices: notices || [],
        timestamp: new Date(),
      }]);
      streamingTextRef.current = '';
      streamingSourcesRef.current = [];
      setStreamingText('');
      setStreamingSources([]);
      setSourcesPending(false);
      setIsStreaming(false);

      const convId = conversationIdRef.current;
      if (convId && content) {
        try {
          await apiClient.appendConversationMessage(convId, {
            role: 'assistant',
            content,
            metadata: {
              queries: queriesExecuted || [],
              suggestions: suggestions || [],
              sources: resolvedSources || [],
              trust: trust || null,
              notices: notices || [],
              persona,
            },
          });
          turnCountRef.current += 1;
          if (turnCountRef.current === 4) {
            requestAutoTitle(convId);
          }
          refreshConversations();
        } catch (err) {
          console.error('Failed to persist assistant message:', err);
        }
      }
    });

    const unsubError = onError(({ message, code }) => {
      // Prefer the opaque code (quota_exceeded, provider_not_allowed,
      // policy_unavailable — websocket/handler.js) over its English prose:
      // classifyError() matches both, but the code is locale-independent and
      // never shifts if the server's message wording changes.
      setError(code || message);
      setIsStreaming(false);
      setSourcesPending(false);
      // The partial answer stays on screen (spec §11.1) — a technical error
      // must never swallow what the user could already read. It is persisted
      // too, otherwise a reload or a thread switch (which rehydrates from the
      // server) would lose exactly what §11.1 says must survive.
      if (streamingTextRef.current) {
        const partialContent = streamingTextRef.current;
        const partialSources = streamingSourcesRef.current;
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: partialContent,
          persona: personaStamp(),
          sources: partialSources,
          partial: true,
          timestamp: new Date(),
        }]);
        persistInterruptedTurn(partialContent, partialSources, { partial: true });
      }
      streamingTextRef.current = '';
      setStreamingText('');
    });

    return () => {
      unsubChunk();
      unsubComplete();
      unsubError();
    };
  }, [onChunk, onComplete, onError, requestAutoTitle, refreshConversations, persistInterruptedTurn, personaStamp]);

  const askPersona = useCallback(async (question, context, convId) => {
    const history = messagesRef.current
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }));
    // On a retry the user turn is already in state; don't send it twice.
    const last = history[history.length - 1];
    if (!(last && last.role === 'user' && last.content === question)) {
      history.push({ role: 'user', content: question });
    }

    setIsStreaming(true);
    setError(null);

    try {
      const response = await apiClient.personaChat({
        circleId: selectedPersona.circleId,
        personaId: selectedPersona.personaId,
        messages: history,
        conversationId: personaConversationIdRef.current || undefined,
      });

      if (!response?.ok) {
        const msg = response?.error?.message || response?.error || 'Persona chat failed';
        throw new Error(typeof msg === 'string' ? msg : 'Persona chat failed');
      }

      // Track nyxCore conversation id for follow-ups (non-blocking PATCH)
      if (response.conversationId) {
        personaConversationIdRef.current = response.conversationId;
        if (convId) {
          fetch(`/api/conversations/${convId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ personaConversationId: response.conversationId }),
          }).catch(err => console.warn('PATCH personaConversationId failed:', err));
        }
      }

      const assistantContent = response.content || '';
      const billed = response.usage ? {
        amount: response.usage.billedAmount,
        currency: response.usage.currency,
        inputTokens: response.usage.inputTokens,
        outputTokens: response.usage.outputTokens,
      } : null;
      // The persona path still returns the legacy ad-hoc Axiom diagnostics and
      // emits no [S1] markers — render it as such, never as fake citations.
      const axiom = response.axiom || null;

      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: assistantContent,
        persona: { id: selectedPersona.personaId, name: selectedPersona.name },
        timestamp: new Date(),
        billed,
        axiom,
        sources: [],
        trust: null,
      }]);
      setIsStreaming(false);

      // Persist async (non-blocking)
      if (convId && assistantContent) {
        apiClient.appendConversationMessage(convId, {
          role: 'assistant',
          content: assistantContent,
          metadata: { billed, axiom, persona: { id: selectedPersona.personaId, name: selectedPersona.name } },
        }).then(() => {
          turnCountRef.current += 1;
          if (turnCountRef.current === 4) requestAutoTitle(convId);
          refreshConversations();
        }).catch(err => console.error('Failed to persist persona response:', err));
      }
    } catch (err) {
      console.error('Persona chat error:', err);
      setIsStreaming(false);
      setError(err.message);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: '',
        persona: { id: selectedPersona.personaId, name: selectedPersona.name },
        timestamp: new Date(),
        isError: true,
        errorMessage: err.message || null,
      }]);
      toast.error(err.message || 'Persona chat error');
    }
  }, [selectedPersona, requestAutoTitle, refreshConversations, toast]);

  const dispatch = useCallback((question, context, scope, convId) => {
    const usePersona = !!(selectedPersona?.circleId || selectedPersona?.personaId);
    streamingTextRef.current = '';
    streamingSourcesRef.current = [];
    setStreamingText('');
    setStreamingSources([]);
    setError(null);

    if (usePersona) {
      askPersona(question, context, convId);
    } else {
      setIsStreaming(true);
      setSourcesPending(true);
      sendMessage(question, context, SCOPE_LENS[scope] || null);
    }
  }, [selectedPersona, askPersona, sendMessage]);

  const ask = useCallback(async (question, context = null, options = {}) => {
    const scope = options.scope || 'all';
    const usePersona = !!(selectedPersona?.circleId || selectedPersona?.personaId);
    if (typeof question !== 'string' || !question.trim()) return;
    if (!usePersona && !connected) {
      setError('offline');
      toast.error('Backend nicht verbunden. Bitte kurz warten oder Persona wählen.');
      return;
    }

    let convId = conversationIdRef.current;
    if (!convId) {
      try {
        // Suppress the next currentId-change reload — we just created an empty
        // conversation and don't need to fetch it (would race with our local
        // setMessages append + the assistant response).
        skipNextReloadRef.current = true;
        const conv = await createConversation(question.slice(0, 60));
        convId = conv.id;
        conversationIdRef.current = convId;
      } catch (err) {
        skipNextReloadRef.current = false;
        console.error('Failed to create conversation:', err);
      }
    }

    // The optimistic turn carries the same attribution the server stamps from
    // the bearer token, so the label doesn't change under the reader when the
    // thread is reloaded.
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      content: question,
      author: { id: user?.id || null, name: user?.username || null },
      timestamp: new Date(),
    }]);

    if (convId) {
      apiClient.appendConversationMessage(convId, {
        role: 'user',
        content: question,
        metadata: context ? { context, scope } : { scope },
      })
        .then(() => { turnCountRef.current += 1; })
        .catch(err => console.error('Failed to persist user message:', err));
    }

    lastRequestRef.current = { question, context, scope };
    dispatch(question, context, scope, convId);
  }, [connected, selectedPersona, createConversation, dispatch, toast, user]);

  /**
   * Resume the last question after an error, a cancellation or an offline
   * blip. The user turn stays where it is; only the failed answer is replaced.
   */
  const retry = useCallback(() => {
    const last = lastRequestRef.current;
    if (!last) return;
    setMessages(prev => {
      const next = [...prev];
      while (next.length && next[next.length - 1].role === 'assistant'
        && (next[next.length - 1].isError || next[next.length - 1].cancelled || next[next.length - 1].partial)) {
        next.pop();
      }
      return next;
    });
    dispatch(last.question, last.context, last.scope, conversationIdRef.current);
  }, [dispatch]);

  const cancel = useCallback(() => {
    cancelStream();
    setIsStreaming(false);
    setSourcesPending(false);

    if (streamingTextRef.current) {
      const partialContent = streamingTextRef.current;
      const partialSources = streamingSourcesRef.current;
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: partialContent,
        persona: personaStamp(),
        sources: partialSources,
        cancelled: true,
        timestamp: new Date(),
      }]);
      persistInterruptedTurn(partialContent, partialSources, { partial: true, cancelled: true });
    }

    streamingTextRef.current = '';
    streamingSourcesRef.current = [];
    setStreamingText('');
    setStreamingSources([]);
  }, [cancelStream, persistInterruptedTurn, personaStamp]);

  /**
   * Thumbs feedback. There is no feedback endpoint yet, so this is view state:
   * it never claims to have been sent anywhere.
   */
  const rateMessage = useCallback((messageId, rating) => {
    setMessages(prev => prev.map(m => (
      m.id === messageId ? { ...m, feedback: m.feedback === rating ? null : rating } : m
    )));
  }, []);

  useEffect(() => {
    if (!connected) return;
    const params = new URLSearchParams(window.location.search);
    const question = params.get('question');
    if (question) {
      ask(question);
      params.delete('question');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [connected]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreamingText('');
    setStreamingSources([]);
    streamingTextRef.current = '';
    streamingSourcesRef.current = [];
    setError(null);
    setCurrentId(null);
    turnCountRef.current = 0;
    personaConversationIdRef.current = null;
    lastRequestRef.current = null;
  }, [setCurrentId]);

  return {
    messages,
    isStreaming,
    streamingText,
    streamingSources,
    sourcesPending,
    error,
    connected,
    conversationId: currentId,
    canRetry: !!lastRequestRef.current,
    ask,
    retry,
    cancel,
    rateMessage,
    clearMessages,
    // Allow input whenever we're not mid-stream. The actual "can we deliver?"
    // gate lives inside `ask` (silently returns if no persona AND no socket),
    // but blocking the textarea here makes the UI feel dead in axiom-only
    // sessions where the WebSocket connect lags or no persona is picked yet.
    canSend: !isStreaming,
  };
}

export default useChat;
